import React, { useEffect, useState } from "react";
import {
  getAIPromptMastersList,
  deleteAIPromptMaster,
} from "../../services/aiPromptMastersService";
import { fetchPageMasters } from "../../services/pageMasterService";
import { paginateData, calculateTotalPages, BootstrapTooltip } from "../../assets/js/script";
import TableHeader from "../Common/TableComponent/TableHeader";
import TableToolbar from "../Common/TableComponent/TableToolbar";
import TablesRow from "../Common/TableComponent/TablesRow";
import { renderStatusBadge } from "../Common/TableComponent/tableBadges";
import { Pagination } from "../Common/TableComponent/Pagination";
import { AddAIPrompt } from "./AddAIPrompt";
import { Loading } from "../Common/OtherElements/Loading";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";
import { confirmDelete } from "../Common/OtherElements/confirmDeleteClone";
import { TableDataStatusError } from "../Common/OtherElements/TableDataStatusError";
import { handleErrors } from "../../utils/errorHandler";
import { usePageLevelAccess } from "../../hooks/usePageLevelAccess";

const DESC_TRUNCATE = 60;

const getPromptId = (item) => item.id ?? item.Id;

const getPageId = (item) => item.pageId ?? item.PageId;

const getPromptName = (item) => item.promptName || item.PromptName || "-";

const getPromptDescription = (item) =>
  item.promptDescription || item.PromptDescription || "";

const isDefaultPrompt = (item) => {
  const value = item.isDefault ?? item.IsDefault;
  return value === true || value === "true" || value === 1;
};

const truncateText = (text, max = DESC_TRUNCATE) => {
  if (!text) return "-";
  if (text.length <= max) return text;
  return `${text.substring(0, max)}...`;
};

const renderIsDefaultBadge = (item) => {
  const isDefault = isDefaultPrompt(item);
  return (
    <span className={`badge badge-soft-${isDefault ? "success" : "secondary"}`}>
      {isDefault ? "Default" : "Non Default"}
    </span>
  );
};

const renderDescriptionCell = (text) => {
  const full = text || "-";
  const display = truncateText(full);
  if (full === "-" || full.length <= DESC_TRUNCATE) return display;
  return (
    <BootstrapTooltip title={full}>
      <span style={{ cursor: "pointer" }}>{display}</span>
    </BootstrapTooltip>
  );
};

export const ManageAIPromptsContent = () => {
  const [pageAccessDetails, setPageAccessDetails] = useState({});
  const pageLevelAccessUrl = "ai-prompt-masters";
  const navigate = useNavigate();
  const { pageAccessData } = usePageLevelAccess(pageLevelAccessUrl);
  const [entriesPerPage, setEntriesPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [promptsList, setPromptsList] = useState([]);
  const [pageMastersList, setPageMastersList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPrompt, setSelectedPrompt] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    if (pageAccessData) {
      if (!pageAccessData.addAccess && !pageAccessData.viewAccess) {
        navigate("/404-error-page");
      } else {
        setPageAccessDetails(pageAccessData);
      }
    }
  }, [pageAccessData, navigate]);

  const fetchPages = async () => {
    try {
      const list = await fetchPageMasters();
      setPageMastersList(Array.isArray(list) ? list : []);
    } catch (error) {
      handleErrors(error);
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await getAIPromptMastersList();
      const list = response.data?.result || response.data || [];
      setPromptsList(Array.isArray(list) ? list : []);
    } catch (error) {
      handleErrors(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPages();
    fetchData();
  }, []);

  const resolvePageName = (item) => {
    const embedded =
      item.pageName || item.PageName || item.pageLink || item.PageLink;
    if (embedded) return embedded;

    const pageId = getPageId(item);
    const page = pageMastersList.find((p) => (p.id ?? p.Id) === pageId);
    if (page) {
      return page.pageName || page.PageName || page.pageLink || "-";
    }
    return pageId ? `Page #${pageId}` : "-";
  };

  const filteredData = promptsList.filter((item) => {
    const pageName = resolvePageName(item).toLowerCase();
    const name = getPromptName(item).toLowerCase();
    const desc = getPromptDescription(item).toLowerCase();
    const query = searchQuery.toLowerCase();
    return (
      pageName.includes(query) ||
      name.includes(query) ||
      desc.includes(query)
    );
  });

  const currentData = paginateData(filteredData, currentPage, entriesPerPage);
  const totalPages = calculateTotalPages(filteredData.length, entriesPerPage);

  const handleEntriesChange = (value) => {
    setEntriesPerPage(value);
    setCurrentPage(1);
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const handleDelete = async (promptId) => {
    const confirmed = await confirmDelete("AI Prompt");
    if (confirmed) {
      try {
        await deleteAIPromptMaster(promptId);
        setPromptsList((prev) =>
          prev.filter((item) => getPromptId(item) !== promptId)
        );
        Swal.fire(
          "Deleted!",
          "The AI prompt has been deleted successfully.",
          "success"
        );
        fetchData();
      } catch (error) {
        handleErrors(error);
      }
    }
  };

  return (
    <>
      {pageAccessDetails.addAccess ? (
        <AddAIPrompt
          editMode={editMode}
          initialData={selectedPrompt ?? {}}
          onSuccess={fetchData}
          setSelectedPrompt={setSelectedPrompt}
          setEditMode={setEditMode}
        />
      ) : (
        ""
      )}

      {pageAccessDetails.viewAccess ? (
        <div className="row">
          <div className="col-xxl-12">
            <div className="card mt-xxl-n5">
              <div className="card-header">
                <h5 className="mb-sm-2 mt-sm-2">Manage AI Prompt Masters</h5>
              </div>
              <div className="card-body manage-amenity-master-card-body">
                <TableToolbar
                  entriesPerPage={entriesPerPage}
                  onEntriesChange={handleEntriesChange}
                  searchValue={searchQuery}
                  searchPlaceholder="Search..."
                  onSearchChange={(e) => {
                    setSearchQuery(e.target.value);
                    setCurrentPage(1);
                  }}
                />

                {loading ? (
                  <Loading />
                ) : (
                  <div className="table-responsive">
                    <table className="table align-middle table-bordered">
                      <TableHeader
                        columns={[
                          "Sl No",
                          "Page Name",
                          "Prompt Name",
                          "Prompt Description",
                          "Is Default",
                          "Status",
                          "Action",
                        ]}
                      />
                      <tbody className="manage-page-group-table-values">
                        {currentData.length === 0 ? (
                          <TableDataStatusError colspan="7" />
                        ) : (
                          currentData.map((item, index) => (
                            <TablesRow
                              key={getPromptId(item)}
                              rowData={{
                                slNo:
                                  (currentPage - 1) * entriesPerPage +
                                  index +
                                  1,
                                pageName: resolvePageName(item),
                                promptName: getPromptName(item),
                                promptDescription: renderDescriptionCell(
                                  getPromptDescription(item)
                                ),
                                isDefault: renderIsDefaultBadge(item),
                                status: renderStatusBadge(item),
                              }}
                              columns={[
                                "slNo",
                                "pageName",
                                "promptName",
                                "promptDescription",
                                "isDefault",
                                "status",
                              ]}
                              hideIcons={false}
                              onEdit={() => {
                                setSelectedPrompt(item);
                                setEditMode(true);
                                window.scrollTo({ top: 0, behavior: "smooth" });
                              }}
                              onDelete={() => handleDelete(getPromptId(item))}
                              pageLevelAccessData={pageAccessDetails}
                            />
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                )}

                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  totalEntries={filteredData.length}
                  entriesPerPage={entriesPerPage}
                  onPageChange={handlePageChange}
                />
              </div>
            </div>
          </div>
        </div>
      ) : (
        ""
      )}
    </>
  );
};
